import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import instance from "../../service/apiService";
import { ApiConstants } from "../../config";

// Deliberately a separate slice/file from bulkValuationSlice.ts — that one is the
// existing "map an Assyst Provider to an Investment/Data Provider" feature
// (registered under state.bulkValuation); reusing its name here would collide.

interface GenerateBulkValuationResult {
  jobId: string;
  status: "IN_PROGRESS";
}

export interface UploadProviderOption {
  dataProviderName: string;
  investmentProviderName: string;
}

// Drives the provider dropdown generically — any upload-eligible "Manual-<X>"
// DataProvider (Elevate today, Finio/Transact later) shows up here with no UI
// change, keyed off its InvestmentProvider display name.
export const fetchUploadProviders = createAsyncThunk<
  UploadProviderOption[],
  void,
  { rejectValue: string }
>(
  "bulkValuationUpload/fetchUploadProviders",
  async (_, { rejectWithValue }) => {
    try {
      const response = await instance.get(
        ApiConstants.orchestration.bulkValuationUploadProviders
      );
      return response.data?.data ?? [];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to load upload providers"
      );
    }
  }
);

export const generateBulkValuation = createAsyncThunk<
  GenerateBulkValuationResult,
  { objectId: string; provider: string; fileName?: string },
  { rejectValue: string }
>(
  "bulkValuationUpload/generateBulkValuation",
  async ({ objectId, provider, fileName }, { rejectWithValue }) => {
    try {
      const response = await instance.post(
        ApiConstants.orchestration.bulkValuationElevateGenerate,
        { objectId, provider, fileName }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to start bulk valuation processing"
      );
    }
  }
);

// generateBulkValuation's own loading/error deliberately isn't tracked here —
// BulkValuationUploadPage is its only consumer and already tracks that with
// local state (submitting/banner), which it actually reads. A duplicate slice
// copy nobody read was left permanently stale on failure.
interface BulkValuationUploadState {
  providers: UploadProviderOption[];
  providersLoading: boolean;
  providersError: string | null;
}

const initialState: BulkValuationUploadState = {
  providers: [],
  providersLoading: false,
  providersError: null,
};

const bulkValuationUploadSlice = createSlice({
  name: "bulkValuationUpload",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUploadProviders.pending, (state) => {
        state.providersLoading = true;
        state.providersError = null;
      })
      .addCase(fetchUploadProviders.fulfilled, (state, action) => {
        state.providersLoading = false;
        state.providers = action.payload;
      })
      .addCase(fetchUploadProviders.rejected, (state, action) => {
        state.providersLoading = false;
        state.providersError = action.payload ?? "Failed to load upload providers";
      });
  },
});

export default bulkValuationUploadSlice.reducer;
